import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  App,
  Button,
  Card,
  Col,
  Divider,
  Drawer,
  Empty,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useAuth } from "../../../context/AuthContext";
import { useThemeStyles } from "../../../context/ThemeContext";
import { getMyUsage, getMyBudget, listMyBudgetRequests, cancelMyBudgetRequest } from "../../../services/api";
import PageHeader from "../../../components/PageHeader";
import { pageShellStyle } from "../../../theme";
import { formatPct, formatUsd, periodOptions } from "../utils";
import RequestExtensionModal from "../components/RequestExtensionModal";

const { Text } = Typography;

export default function MyUsagePage() {
  const { token } = useAuth();
  const themeStyles = useThemeStyles();
  const { message } = App.useApp();
  const [period, setPeriod] = useState("monthly");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerProjectId, setDrawerProjectId] = useState(null);
  const [drawerProjectName, setDrawerProjectName] = useState(null);
  const [drawerBudget, setDrawerBudget] = useState(null);
  const [drawerRequests, setDrawerRequests] = useState([]);
  const [requestOpen, setRequestOpen] = useState(false);

  const budgetDetailStyle = { color: themeStyles.textPrimary, fontSize: 14 };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await getMyUsage(token, period);
      setSummary(res?.data || null);
    } catch (e) {
      message.error(e?.response?.data?.detail || "Failed to load usage");
    } finally {
      setLoading(false);
    }
  }, [token, period, message]);

  useEffect(() => {
    load();
  }, [load]);

  const projectNameById = useMemo(() => {
    const map = new Map();
    for (const p of summary?.projects || []) {
      if (p?.project_id) {
        map.set(p.project_id, p.name || p.project_id);
      }
    }
    return map;
  }, [summary]);

  const openProject = useCallback(
    async (projectId, projectName = null) => {
      if (!token || !projectId) return;
      setDrawerProjectId(projectId);
      setDrawerProjectName(projectName || projectNameById.get(projectId) || projectId);
      setDrawerOpen(true);
      setDrawerBudget(null);
      setDrawerRequests([]);
      try {
        const [budgetRes, reqRes] = await Promise.all([
          getMyBudget(token, projectId, period),
          listMyBudgetRequests(token, projectId, 100),
        ]);
        setDrawerBudget(budgetRes?.data || null);
        if (budgetRes?.data?.name) {
          setDrawerProjectName(budgetRes.data.name);
        }
        setDrawerRequests(Array.isArray(reqRes?.data?.requests) ? reqRes.data.requests : []);
      } catch (e) {
        message.error(e?.response?.data?.detail || "Failed to load project details");
      }
    },
    [token, period, message, projectNameById],
  );

  const projects = summary?.projects || [];
  const totalSpend = summary?.total_spend_usd ?? 0;

  const columns = useMemo(
    () => [
      {
        title: "Project",
        dataIndex: "name",
        key: "name",
        render: (name, row) => {
          const label = name || row?.project_id || "—";
          return (
            <Button type="link" onClick={() => openProject(row.project_id, label)} style={{ padding: 0, fontWeight: 600 }}>
              {label}
            </Button>
          );
        },
      },
      {
        title: "Spend",
        dataIndex: "total_usd",
        key: "total_usd",
        width: 140,
        render: (v) => <Text style={budgetDetailStyle}>{formatUsd(v)}</Text>,
      },
      {
        title: "Events",
        dataIndex: "events",
        key: "events",
        width: 90,
        render: (v) => <Text style={budgetDetailStyle}>{v}</Text>,
      },
    ],
    [openProject, budgetDetailStyle],
  );

  const budgetSummary = drawerBudget?.budget_summary;
  const pendingRequest = drawerBudget?.pending_request;
  const drawerTitle = drawerProjectName || drawerProjectId || "Project details";

  const eligibleProjectIds = useMemo(
    () => (projects || []).filter((p) => p?.can_request_extension).map((p) => p.project_id),
    [projects],
  );

  const drawerCanRequest = Boolean(budgetSummary?.can_request_extension);
  const drawerBlockReason =
    budgetSummary?.extension_request_block_reason
    || "Extension requests are available when you reach 80% of your allocated budget.";

  const headerCanRequest = eligibleProjectIds.length > 0;
  const headerBlockReason =
    projects.length === 0
      ? "No assigned projects."
      : "No project has reached 80% of your allocated budget yet.";

  const requestExtensionButton = (enabled, blockReason, onClick) => (
    <Tooltip title={enabled ? "Request additional budget from an administrator" : blockReason}>
      <span>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          disabled={!enabled}
          onClick={onClick}
        >
          Request extension
        </Button>
      </span>
    </Tooltip>
  );

  const cancelPending = async (requestId) => {
    if (!token || !requestId) return;
    try {
      await cancelMyBudgetRequest(token, requestId);
      message.success("Request cancelled");
      if (drawerProjectId) openProject(drawerProjectId, drawerProjectName);
    } catch (e) {
      message.error(e?.response?.data?.detail || "Failed to cancel request");
    }
  };

  return (
    <div style={{ ...pageShellStyle, padding: 18 }}>
      <PageHeader
        title="My Usage"
        actions={(
          <Space wrap>
            <Select
              value={period}
              onChange={setPeriod}
              options={periodOptions}
              style={{ width: 170 }}
            />
            {requestExtensionButton(headerCanRequest, headerBlockReason, () => setRequestOpen(true))}
          </Space>
        )}
      />

      <Row gutter={[12, 12]}>
        <Col span={24}>
          <Card loading={loading}>
            <Space direction="vertical" style={{ width: "100%" }} size={10}>
              <Text type="secondary">
                Window: {period === "daily" ? "1 day" : period === "90days" ? "90 days" : "30 days"}
              </Text>
              <Text style={{ fontSize: 18, fontWeight: 700, color: themeStyles.textPrimary }}>
                Total: {formatUsd(totalSpend)}
              </Text>
              {projects.length === 0 ? (
                <Empty description="No assigned projects found" />
              ) : (
                <Table
                  rowKey={(r) => r.project_id}
                  columns={columns}
                  dataSource={projects}
                  pagination={{ pageSize: 8 }}
                />
              )}
            </Space>
          </Card>
        </Col>
      </Row>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={drawerTitle}
        width={520}
      >
        {!drawerProjectId ? null : (
          <Space direction="vertical" style={{ width: "100%" }} size={12}>
            <Card size="small" title="Budget">
              {budgetSummary ? (
                <Space direction="vertical" style={{ width: "100%" }} size={8}>
                  <Text style={budgetDetailStyle}>
                    <Text strong style={budgetDetailStyle}>Allocated: </Text>
                    {budgetSummary.allocated_budget_usd == null ? "—" : formatUsd(budgetSummary.allocated_budget_usd)}
                  </Text>
                  <Text style={budgetDetailStyle}>
                    <Text strong style={budgetDetailStyle}>Spent: </Text>
                    {formatUsd(budgetSummary.spent_usd)}
                  </Text>
                  <Text style={budgetDetailStyle}>
                    <Text strong style={budgetDetailStyle}>Remaining: </Text>
                    {budgetSummary.remaining_usd == null ? "—" : formatUsd(budgetSummary.remaining_usd)}
                  </Text>
                  <Text style={budgetDetailStyle}>
                    <Text strong style={budgetDetailStyle}>Utilization: </Text>
                    {budgetSummary.utilization_pct == null ? "—" : formatPct(budgetSummary.utilization_pct)}
                  </Text>
                  {!budgetSummary.enabled ? (
                    <Tag color="default">Budget disabled</Tag>
                  ) : budgetSummary.block_when_exceeded ? (
                    <Tag color="red">Blocking enabled</Tag>
                  ) : (
                    <Tag color="gold">Warnings only</Tag>
                  )}
                  {requestExtensionButton(drawerCanRequest, drawerBlockReason, () => setRequestOpen(true))}
                </Space>
              ) : (
                <Space direction="vertical" style={{ width: "100%" }} size={8}>
                  <Text style={budgetDetailStyle}>No budget configured for you in this project.</Text>
                  {requestExtensionButton(false, drawerBlockReason, () => setRequestOpen(true))}
                </Space>
              )}
            </Card>

            <Card size="small" title="Extension requests">
              {pendingRequest ? (
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Tag color="blue">Pending</Tag>
                  <Text style={budgetDetailStyle}>
                    Requested: {formatUsd(pendingRequest.requested_amount_usd)} — {pendingRequest.reason}
                  </Text>
                  <Button type="link" onClick={() => cancelPending(pendingRequest.request_id)} style={{ padding: 0 }}>
                    Cancel pending request
                  </Button>
                  <Divider style={{ margin: "10px 0" }} />
                </Space>
              ) : null}
              {drawerRequests.length === 0 ? (
                <Text type="secondary">No requests.</Text>
              ) : (
                <Table
                  size="small"
                  rowKey={(r) => r.request_id}
                  pagination={{ pageSize: 6 }}
                  columns={[
                    {
                      title: "Created",
                      dataIndex: "created_at",
                      key: "created_at",
                      width: 160,
                      render: (v) => (
                        <Text style={budgetDetailStyle}>
                          {String(v || "—").replace("T", " ").slice(0, 19)}
                        </Text>
                      ),
                    },
                    {
                      title: "Amount",
                      dataIndex: "requested_amount_usd",
                      key: "requested_amount_usd",
                      width: 110,
                      render: (v) => <Text style={budgetDetailStyle}>{formatUsd(v)}</Text>,
                    },
                    {
                      title: "Status",
                      dataIndex: "status",
                      key: "status",
                      width: 110,
                      render: (v) => <Tag>{String(v || "—")}</Tag>,
                    },
                  ]}
                  dataSource={drawerRequests}
                />
              )}
            </Card>
          </Space>
        )}
      </Drawer>

      <RequestExtensionModal
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        defaultProjectId={
          drawerCanRequest && drawerProjectId
            ? drawerProjectId
            : headerCanRequest
              ? eligibleProjectIds[0] || null
              : null
        }
        eligibleProjectIds={eligibleProjectIds}
        onCreated={() => {
          load();
          if (drawerProjectId) openProject(drawerProjectId, drawerProjectName);
        }}
      />
    </div>
  );
}
